import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { navigate, routeFor, setFilters } from '../router.js';
import { DEFAULTS as FILTER_DEFAULTS } from '../lib/filters.js';
import { HEATMAP_DEFAULT_DIMENSION } from '../lib/heatmap.js';
import { showsCard, visibleRenderings } from '../lib/expert.js';

/**
 * The ten destinations of PRD 9 exist from Phase 19 so the frame can be reviewed whole: the
 * navigation, the filter bar, the banner and the theme are all exercised on every page. Each
 * page says which phase fills it in, because an empty screen with no explanation reads as a bug.
 *
 * As a view lands, its phase replaces the placeholder here with a real page component.
 */
export class PagePlaceholder extends StoreMixin(LitElement) {
  static properties = { params: { type: Array } };

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 28px;
      max-width: 720px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h1 {
      margin: 8px 0 12px;
      font-size: 28px;
      line-height: 1.15;
      color: var(--ink-strong);
    }
    p {
      margin: 0 0 8px;
      color: var(--ink-muted);
      max-width: 60ch;
    }
    code {
      font-family: var(--font-eyebrow);
      font-size: 12px;
      background: var(--surface-2);
      border: 1px solid var(--border-soft);
      border-radius: 4px;
      padding: 1px 5px;
    }
  `;

  /** Overridden by each page below. */
  get nav() {
    return { label: 'Page', phase: null };
  }

  get body() {
    return html`<p>This view arrives in Phase ${this.nav.phase} of the development plan.</p>`;
  }

  render() {
    const { filters } = this.appState;
    return html`
      <div class="card">
        <div class="eyebrow">Phase ${this.nav.phase}</div>
        <h1>${this.nav.label}</h1>
        ${this.body}
        <p>
          The shell around it is live: the filters above are shared state, and this page is reading
          them — currently <code>${filters.dimension}</code> over
          <code>${filters.weeks} weeks</code>, opportunities <code>${filters.mode}</code>.
        </p>
      </div>
    `;
  }
}

const navFor = (path) => routeFor(path) ?? { label: 'Page', phase: null };

/** The two readings of the primary chart (PRD 10.1, 10.7). */
const DASHBOARD_VIEWS = [
  { key: 'demand', label: 'Demand', hint: 'What is asked of each week, against capacity' },
  {
    key: 'bench',
    label: 'Bench',
    hint: 'What is left of each week, by role, against the pipeline',
  },
];

/**
 * The dashboard (PRD 10.1). Top to bottom as the sketch draws it: the KPI strip and the three
 * risk panels (Phase 22, one `dashboard-summary` card), the demand ribbon (Phase 20) as the
 * primary chart with the Demand / Bench toggle the sketch puts beside it, and the capacity
 * bridge (Phase 21) beneath. Bench (Phase 29, PRD 10.7) swaps the ribbon *and* the bridge for
 * the bench card: the bridge is the demand reading's companion (its steps wear the ribbon's
 * layer colours), and the bench wears the same hue slots for roles, so the two vocabularies
 * must never be on screen together.
 *
 * The ribbon and the bridge stay mounted while the bench is showing, hidden rather than removed,
 * so switching back costs no request; the bench card mounts on first use. The reading is page
 * state, not a filter: the bench has its own destination at `/bench` for a link.
 */
class PageDashboard extends PagePlaceholder {
  static properties = { _view: { state: true } };

  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        display: grid;
        gap: 18px;
      }
      .view-toggle {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px 14px;
        margin-bottom: -6px;
      }
      .segmented {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented button {
        font: inherit;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        background: none;
        border: 0;
        border-radius: var(--radius-pill);
        padding: 4px 12px;
        cursor: pointer;
      }
      .segmented button[aria-pressed='true'] {
        /* --ink-strong is near-white in the dark theme, so the label takes the surface colour
           rather than --ink-on-brand, which is also near-white: white on white otherwise. */
        color: var(--surface);
        background: var(--ink-strong);
      }
      .view-toggle .hint {
        font-size: 12px;
        color: var(--ink-muted);
      }
      [hidden] {
        display: none;
      }
    `,
  ];

  constructor() {
    super();
    this._view = 'demand';
  }

  get nav() {
    return navFor('/');
  }

  render() {
    const bench = this._view === 'bench';
    const current = DASHBOARD_VIEWS.find((v) => v.key === this._view) ?? DASHBOARD_VIEWS[0];
    return html`
      <div class="stack">
        <dashboard-summary></dashboard-summary>
        <div class="view-toggle" role="group" aria-label="Primary chart">
          <span class="segmented">
            ${DASHBOARD_VIEWS.map(
              (v) => html`
                <button
                  type="button"
                  data-view=${v.key}
                  aria-pressed=${this._view === v.key ? 'true' : 'false'}
                  title=${v.hint}
                  @click=${() => (this._view = v.key)}
                >
                  ${v.label}
                </button>
              `,
            )}
          </span>
          <span class="hint" id="view-hint">${current.hint}</span>
        </div>
        <demand-ribbon ?hidden=${bench}></demand-ribbon>
        ${
          showsCard('capacity-bridge', this.appState.expert)
            ? html`<capacity-bridge ?hidden=${bench}></capacity-bridge>`
            : nothing
        }
        ${bench ? html`<bench-forecast></bench-forecast>` : nothing}
      </div>
    `;
  }
}
customElements.define('page-dashboard', PageDashboard);

/**
 * The timeline (PRD 10.2; Phase 26). One card, which fetches its own
 * `GET /forecast/timeline`: unlike the heatmap page, nothing else here reads that payload, so
 * there is no reason for the page to hold it. The ghost bars of Phase 27 arrive on this card,
 * from the compare run already in the shared filters.
 */
/**
 * The timeline (PRD 10.2; Phase 26), with the decision calendar beneath it (V-17; Phase 49).
 *
 * Two time axes over the same window, and they answer consecutive questions. The Gantt says when
 * the work is; the calendar says when the *decisions* about it are due, which is a different
 * calendar and usually an earlier one — a November overload is a freelancer conversation in
 * October and a requisition that needed opening in August. Reading the second under the first is
 * the whole point: the bars show the problem arriving, the markers show how long ago you should
 * have started doing something about it.
 *
 * The two share nothing but the page and the filter bar. The timeline reads
 * `GET /forecast/timeline`, the calendar `GET /forecast/decisions`, whose dates are recomputed
 * from current settings rather than read back from the run.
 */
class PageTimeline extends PagePlaceholder {
  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        /* minmax(0, 1fr), not the default auto: the timeline scrolls its grid inside its own
           card, and an auto column would let that grid widen the page instead. */
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
      }
    `,
  ];

  get nav() {
    return navFor('/timeline');
  }

  render() {
    return html`<div class="stack">
      <project-timeline></project-timeline>
      <decision-calendar></decision-calendar>
    </div>`;
  }
}
customElements.define('page-timeline', PageTimeline);

/** The six renderings of the same matrix the reader can choose between (PRD 10.3). */
const HEATMAP_RENDERINGS = [
  { key: 'matrix', label: 'Matrix', hint: 'Allocation as colour, row by week' },
  { key: 'multiples', label: 'Small multiples', hint: 'One sparkline per row, on one scale' },
  { key: 'horizon', label: 'Horizon', hint: 'One folded band per row, for many rows at once' },
  { key: 'beeswarm', label: 'Beeswarm', hint: 'One dot per row per week, so the spread shows' },
  { key: 'mosaic', label: 'Mosaic', hint: 'One column per row, as wide as its capacity' },
  {
    key: 'bump',
    label: 'Rank',
    hint: 'One line per row: its place in the risk list, week by week',
  },
];

/**
 * The heatmap (PRD 10.3; Phase 23): allocation over capacity per row of the dimension in the
 * filter bar and per week — or per weekday for the 14-day window when the grain is daily — in
 * the threshold colour scale. The small multiples (V-5, Phase 25) are the same matrix drawn as
 * a grid of sparklines, the horizon chart (V-4, Phase 41) is the same matrix folded into one
 * thin band per row, the beeswarm (V-6, Phase 42) is the same matrix as one dot per row per
 * week, which is the only one that shows the spread a ratio hides, and the mosaic (V-7, Phase
 * 43) is the same matrix as one column per row, as wide as that row's capacity, which is the
 * only one that shows overload in hours rather than in ratio, and the bump chart (V-14, Phase 48)
 * is the same matrix as a ranking — one line per row through the weeks — which is the only one
 * that answers whether it is the same row in trouble every week. The PRD offers all of them as
 * alternate renderings the reader selects. Beneath whichever is showing, the bullet rows (V-3,
 * Phase 24) draw one week of it in hours — and the mosaic's own Week span is those same rows.
 *
 * The page owns three things the cards share: the heatmap response (the matrix fetches it and
 * hands it over, so the other cards are the same run's numbers and cost no second request), the
 * selected week (any card can set it; the matrix marks its column, the multiples mark it in
 * every panel, the horizon outlines it in every row, the beeswarm outlines its column, the
 * mosaic draws it when its span is a week, the bullet rows draw it), and which
 * rendering is showing. The week starts unset, which means the bullet rows open on the window's
 * peak.
 *
 * The matrix stays mounted while another rendering is showing, hidden rather than removed: it
 * is the card that holds the request, so unmounting it would empty the others and cost a second
 * fetch every time the reader changed their mind. The rendering is page state, not a filter
 * (Section 13.3's reasoning for the selected week applies to it too).
 */
class PageHeatmap extends PagePlaceholder {
  static properties = {
    _heatmap: { state: true },
    _week: { state: true },
    _rendering: { state: true },
  };

  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        display: grid;
        gap: 18px;
      }
      .view-toggle {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px 14px;
        margin-bottom: -6px;
      }
      .segmented {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented button {
        font: inherit;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        background: none;
        border: 0;
        border-radius: var(--radius-pill);
        padding: 4px 12px;
        cursor: pointer;
      }
      .segmented button[aria-pressed='true'] {
        /* --ink-strong is near-white in the dark theme, so the label takes the surface colour
           rather than --ink-on-brand, which is also near-white: white on white otherwise. */
        color: var(--surface);
        background: var(--ink-strong);
      }
      .view-toggle .hint {
        font-size: 12px;
        color: var(--ink-muted);
      }
      [hidden] {
        display: none;
      }
    `,
  ];

  constructor() {
    super();
    this._heatmap = { data: null, loading: false, error: null };
    this._week = null;
    this._rendering = 'matrix';
  }

  get nav() {
    return navFor('/heatmap');
  }

  /**
   * On arrival with the filters at their default, open by service (lib/heatmap.js). Replaced in
   * place, so the back button does not stop here twice, and only on arrival: a reader who then
   * chooses Company in the bar gets Company.
   */
  connectedCallback() {
    super.connectedCallback();
    if (this.appState.filters.dimension === FILTER_DEFAULTS.dimension)
      setFilters({ dimension: HEATMAP_DEFAULT_DIMENSION }, { replace: true });
  }

  /** The renderings this reader may choose (lib/expert.js), and the one that is showing. */
  get _renderings() {
    return visibleRenderings(HEATMAP_RENDERINGS, this.appState.expert);
  }
  get _shown() {
    const renderings = this._renderings;
    return renderings.some((r) => r.key === this._rendering) ? this._rendering : 'matrix';
  }

  /** A window or dimension change can retire the selected week; the bullet rows then pick again. */
  _onData(detail) {
    this._heatmap = detail;
    const weeks = detail.data?.totals ?? [];
    if (this._week && weeks.length && !weeks.some((w) => w.week_start === this._week))
      this._week = null;
  }

  _toggle() {
    const renderings = this._renderings;
    const shown = this._shown;
    const current = renderings.find((r) => r.key === shown);
    const ways = ['', 'one', 'two', 'three', 'four', 'five', 'six'][renderings.length] ?? 'several';
    return html`
      <div class="view-toggle" role="group" aria-label="Rendering">
        <span class="segmented">
          ${renderings.map(
            (r) =>
              html`<button
                type="button"
                data-rendering=${r.key}
                aria-pressed=${shown === r.key ? 'true' : 'false'}
                @click=${() => (this._rendering = r.key)}
              >
                ${r.label}
              </button>`,
          )}
        </span>
        <span class="hint">${current.hint} — one response, drawn ${ways} ways.</span>
      </div>
    `;
  }

  render() {
    const { data, loading, error } = this._heatmap;
    const shown = this._shown;
    const multiples = shown === 'multiples';
    const horizon = shown === 'horizon';
    const beeswarm = shown === 'beeswarm';
    const mosaic = shown === 'mosaic';
    const bump = shown === 'bump';
    return html`
      <div class="stack">
        ${this._toggle()}
        <capacity-heatmap
          ?hidden=${multiples || horizon || beeswarm || mosaic || bump}
          .week=${this._week}
          @heatmap-data=${(e) => this._onData(e.detail)}
          @heatmap-week=${(e) =>
            (this._week = e.detail.week_start === this._week ? null : e.detail.week_start)}
        ></capacity-heatmap>
        ${
          multiples
            ? html`<small-multiples
                .data=${data}
                .loading=${loading}
                .error=${error}
                .week=${this._week}
                @week-change=${(e) => (this._week = e.detail.week_start)}
              ></small-multiples>`
            : nothing
        }
        ${
          horizon
            ? html`<horizon-chart
                .data=${data}
                .loading=${loading}
                .error=${error}
                .week=${this._week}
                @week-change=${(e) => (this._week = e.detail.week_start)}
              ></horizon-chart>`
            : nothing
        }
        ${
          beeswarm
            ? html`<beeswarm-chart
                .data=${data}
                .loading=${loading}
                .error=${error}
                .week=${this._week}
                @week-change=${(e) => (this._week = e.detail.week_start)}
              ></beeswarm-chart>`
            : nothing
        }
        ${
          mosaic
            ? html`<mosaic-chart
                .data=${data}
                .loading=${loading}
                .error=${error}
                .week=${this._week}
                @week-change=${(e) => (this._week = e.detail.week_start)}
              ></mosaic-chart>`
            : nothing
        }
        ${
          bump
            ? html`<bump-chart
                .data=${data}
                .loading=${loading}
                .error=${error}
                .week=${this._week}
                @week-change=${(e) => (this._week = e.detail.week_start)}
              ></bump-chart>`
            : nothing
        }
        <bullet-rows
          .data=${data}
          .loading=${loading}
          .error=${error}
          .week=${this._week}
          @week-change=${(e) => (this._week = e.detail.week_start)}
        ></bullet-rows>
      </div>
    `;
  }
}
customElements.define('page-heatmap', PageHeatmap);
/**
 * The assignments (PRD 10.6; V-8; Phase 28) and, beneath them, the contention map (V-9;
 * Phase 44).
 *
 * The matrix answers "who is on what this week" one cell at a time; the map answers the question
 * the cells imply and never state — **which projects are competing for the same people** — by
 * pairing them on the hours neither can give up. They are two readings of one week's staffing,
 * which is why they share a page and a selected week: the page owns the week, either card can
 * set it, and in the map's Week span a chord is the minimum of two of the matrix's own cells.
 *
 * Unlike the heatmap page, the two cards fetch separately: the matrix reads
 * `GET /forecast/matrix` for one week, the map reads `GET /forecast/contention`, which takes the
 * per-week minimum the matrix's payload cannot be made to give (it holds one week, and the map's
 * default span is the whole window).
 */
class PageAssignments extends PagePlaceholder {
  static properties = { _week: { state: true } };

  constructor() {
    super();
    this._week = null;
  }

  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        /* minmax(0, 1fr), not auto: the matrix scrolls its grid inside its own card, and an
           auto column would let a hundred columns widen the page instead. */
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
      }
    `,
  ];

  get nav() {
    return navFor('/assignments');
  }

  render() {
    return html`<div class="stack">
      <assignment-matrix
        .week=${this._week}
        @week-change=${(e) => (this._week = e.detail.week_start)}
      ></assignment-matrix>
      ${
        showsCard('contention-map', this.appState.expert)
          ? html`<contention-map
              .week=${this._week}
              @week-change=${(e) => (this._week = e.detail.week_start)}
            ></contention-map>`
          : nothing
      }
    </div>`;
  }
}
customElements.define('page-assignments', PageAssignments);
/**
 * The bench (PRD 10.7; Phase 29): the same card the dashboard's toggle shows, at its own
 * destination so "the bench" is a link. One card, which fetches its own `GET /forecast/bench`.
 */
class PageBench extends PagePlaceholder {
  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
      }
    `,
  ];

  get nav() {
    return navFor('/bench');
  }

  render() {
    return html`<div class="stack"><bench-forecast></bench-forecast></div>`;
  }
}
customElements.define('page-bench', PageBench);

/**
 * The opportunity funnel (PRD 10.4; Phase 33). Not one of PRD 9's ten destinations: it is the
 * bench's pipeline line opened up — which stages that line is made of, what each would ask of
 * us and in which week — so it sits under Bench, which stays lit, and the bench card links here.
 */
class PageFunnel extends PagePlaceholder {
  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
      }
    `,
  ];

  get nav() {
    return navFor('/funnel');
  }

  render() {
    return html`<div class="stack"><opportunity-funnel></opportunity-funnel></div>`;
  }
}
customElements.define('page-funnel', PageFunnel);
/**
 * Changes (PRD 10.8; Design Review V-13; Phase 30): what moved since the last forecast, and
 * why. One card, which fetches its own `GET /forecast/diff` for the run in the filter bar
 * against the run in **Compare** — or, with none chosen, the run one week prior, which is the
 * comparison the PRD says every planning meeting opens on.
 */
class PageChanges extends PagePlaceholder {
  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
      }
    `,
  ];

  get nav() {
    return navFor('/changes');
  }

  render() {
    return html`<div class="stack"><snapshot-changes></snapshot-changes></div>`;
  }
}
customElements.define('page-changes', PageChanges);

/**
 * Project Intelligence (PRD 9, 10.5; Phase 31). Two routes, one destination: `/projects` is the
 * picker — every project the run saw, searchable — and `/projects/:id` is one project's panel.
 * The panel is a route and not a drawer because "where did this number come from" is the
 * question a planner sends to someone else, and a link that cannot be pasted answers nobody.
 */
class PageProjects extends PagePlaceholder {
  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
      }
    `,
  ];

  get nav() {
    return navFor('/projects');
  }

  render() {
    return html`<div class="stack"><project-list></project-list></div>`;
  }
}
customElements.define('page-projects', PageProjects);

class PageProject extends PagePlaceholder {
  static properties = { route: { type: Object } };

  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 12px;
      }
      .crumb {
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .crumb button {
        font: inherit;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
      }
      .crumb button:hover {
        text-decoration: underline;
      }
    `,
  ];

  get nav() {
    return navFor('/projects');
  }

  render() {
    const id = this.route?.params?.id ?? null;
    return html`
      <div class="stack">
        <div class="crumb">
          <button @click=${() => navigate('/projects')}>← All projects</button>
        </div>
        <project-intelligence project-id=${id ?? ''}></project-intelligence>
      </div>
    `;
  }
}
customElements.define('page-project', PageProject);

/**
 * The individual view (PRD 7.2; Phase 32). `/people` is the picker — everyone the signed-in
 * person may see — and `/people/:id` is one person's weeks. Neither is one of the ten
 * destinations of PRD 9: a person is reached from a heatmap or matrix row, and the Heatmap item
 * stays lit. A route and not a drawer, for the reason the project panel is: "how loaded am I
 * next month" is a link a manager sends.
 */
class PagePeople extends PagePlaceholder {
  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
      }
    `,
  ];

  get nav() {
    return navFor('/people');
  }

  render() {
    return html`<div class="stack"><person-list></person-list></div>`;
  }
}
customElements.define('page-people', PagePeople);

class PagePerson extends PagePlaceholder {
  static properties = { route: { type: Object } };

  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 12px;
      }
      .crumb {
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .crumb button {
        font: inherit;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
      }
      .crumb button:hover {
        text-decoration: underline;
      }
    `,
  ];

  get nav() {
    return navFor('/heatmap');
  }

  render() {
    const id = this.route?.params?.id ?? null;
    return html`
      <div class="stack">
        <div class="crumb">
          <button @click=${() => navigate('/people')}>← All people</button>
          <span> · </span>
          <button @click=${() => navigate('/heatmap?dim=individual')}>Heatmap</button>
        </div>
        <person-view person-id=${id ?? ''}></person-view>
      </div>
    `;
  }
}
customElements.define('page-person', PagePerson);
/**
 * Scenarios (PRD 7.5, 9; V-17; Phase 35): the saved what-ifs, what each changes, and the act of
 * showing one. No chart of its own — a scenario is a second set of numbers for the views there
 * already are, and selecting one moves all of them.
 */
/**
 * Scenarios (PRD 7.5; Phase 35), with the sensitivity tornado above the list (V-12; Phase 47).
 *
 * Section 24.3 of the UI Client said this page owns no chart, and the tornado is the exception
 * that proves why: it is not a second set of numbers for a view that already exists, it is the
 * answer to the question the list otherwise leaves to guesswork — *which* what-if is worth
 * building. Every bar is a scenario a planner could construct, drawn before any of them is; the
 * list beneath is where they construct the one that mattered.
 *
 * The two cards share nothing but the page. The tornado reads `GET /forecast/sensitivity` for the
 * run in the filter bar, over that run's whole horizon; the list reads the scenarios.
 */
class PageScenarios extends PagePlaceholder {
  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
      }
    `,
  ];

  get nav() {
    return navFor('/scenarios');
  }

  render() {
    return html`<div class="stack">
      <sensitivity-tornado></sensitivity-tornado>
      <scenario-list></scenario-list>
    </div>`;
  }
}
customElements.define('page-scenarios', PageScenarios);

/**
 * Data Hygiene (PRD 9.3, 10.10, 12.2; V-18; Phase 34): the ranked worklist, the run's quality
 * metrics beneath it — the order the PRD sketches them in, and the order they are read in — and,
 * since Phase 44's sibling V-11, the source flow beneath those. The percentages say how much of
 * the forecast is inferred; the worklist says which missing facts are doing it and who can fix
 * them, so the work comes first and the measurement follows.
 *
 * The source flow (Phase 45) is the third card because it is the second one's figure taken apart:
 * the metrics say the company is 62 per cent explicit, and the flow says which roles that is true
 * of. A planner reading a role's number on the heatmap wants to know whose number to trust, and
 * this is the page where that question already lives.
 *
 * The page holds one thing the cards share: the worklist's response. The metrics card needs it to
 * compare the run's frozen hygiene snapshot with the worklist as it stands now — the only two
 * numbers on the page that can honestly disagree. The source flow fetches its own
 * `GET /forecast/source-flow`, because nothing else on the page holds the allocation rows it
 * groups.
 */
class PageHygiene extends PagePlaceholder {
  static properties = { _worklist: { state: true } };

  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        /* minmax(0, 1fr), not auto: the worklist scrolls its table inside its own card, and an
           auto column would let a long project name widen the page instead. */
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
      }
    `,
  ];

  constructor() {
    super();
    this._worklist = null;
  }

  get nav() {
    return navFor('/hygiene');
  }

  render() {
    const { expert } = this.appState;
    return html`
      <div class="stack">
        <hygiene-worklist @hygiene-data=${(e) => (this._worklist = e.detail)}></hygiene-worklist>
        ${
          showsCard('quality-metrics', expert)
            ? html`<quality-metrics .worklist=${this._worklist}></quality-metrics>`
            : nothing
        }
        ${showsCard('source-flow', expert) ? html`<source-flow></source-flow>` : nothing}
      </div>
    `;
  }
}
customElements.define('page-hygiene', PageHygiene);
/**
 * Settings (Phase 19's placeholder, still). Two controls are real, and both are here for the same
 * reason: something else on the site is unusable without them.
 *
 * The digest subscription (Phase 36) is here because an email nobody can switch off is not a
 * subscription, and the digest's own footer links here. The remedy lead times (Phase 49) are here
 * because every date on the decision calendar is an overload's first week minus one of those four
 * numbers — they are a claim about us rather than about the forecast, the people who know the
 * true figure are not the people who read the API, and a wrong one makes a whole page of dates
 * wrong in the same direction. Writing them is Admin; the card shows everyone else what they are.
 */
class PageSettings extends PagePlaceholder {
  static styles = [
    PagePlaceholder.styles,
    css`
      .stack {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 18px;
        max-width: 720px;
      }
    `,
  ];

  get nav() {
    return navFor('/settings');
  }

  render() {
    return html`
      <div class="stack">
        <div class="card">
          <div class="eyebrow">Phase ${this.nav.phase}</div>
          <h1>${this.nav.label}</h1>
          <p>
            Global settings are administered through the API (<code>GET</code> and
            <code>PATCH</code> <code>/api/v1/settings</code>) and the review utilities at
            <code>/utils</code> until this page lands.
          </p>
        </div>
        <digest-subscription></digest-subscription>
        <remedy-lead-times></remedy-lead-times>
      </div>
    `;
  }
}
customElements.define('page-settings', PageSettings);

class PageNotFound extends PagePlaceholder {
  get nav() {
    return { label: 'Not found', phase: null };
  }
  get body() {
    return html`<p>No view lives at this address.</p>`;
  }
  render() {
    return html`
      <div class="card">
        <div class="eyebrow">404</div>
        <h1>Not found</h1>
        <p>No view lives at <code>${window.location.pathname}</code>.</p>
      </div>
    `;
  }
}
customElements.define('page-not-found', PageNotFound);
